import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';
import { makeTranslationsSchema } from '../common/translations/translations';

import { LegalDocTypeSchema } from './doc-type/doc-type';
import { LegalMaterialitySchema } from './materiality/materiality';
import { LegalVersionStatusSchema } from './version-status/version-status';

/** The per-locale content of one version (N5) — what a member actually reads. */
export const LegalVersionTranslationsSchema = makeTranslationsSchema(
  z
    .object({
      title: z.string().min(1).max(200),
      /**
       * The document body. Large by design — a privacy policy is thousands of
       * words — and stored as authored: the admin editor produces markdown, the
       * clients render it. The ceiling exists so a runaway paste cannot fill a
       * column, not because any real document approaches it.
       */
      body: z.string().min(1).max(200_000),
    })
    .strict(),
);

export type LegalVersionTranslations = z.infer<typeof LegalVersionTranslationsSchema>;

/**
 * One version of a legal document (N16).
 *
 * A version is the unit consent points at: `user_consents.versionId` names the
 * exact text a member accepted, on the date they accepted it, forever. That is
 * the whole reason a published version can never be edited — an editable one
 * would make every consent record a claim about text nobody can reconstruct.
 */
export const LegalDocumentVersionSchema = z
  .object({
    id: UuidSchema,
    documentId: UuidSchema,
    /** Monotonic per document, starting at 1. */
    version: z.number().int().min(1),
    status: LegalVersionStatusSchema,
    /** Chosen at publish; `null` while the version is a draft. */
    materiality: LegalMaterialitySchema.nullable(),
    /** When the version takes effect — `null` while unpublished. */
    effectiveAt: z.iso.datetime().nullable(),
    publishedByUserId: UuidSchema.nullable(),
    /** The active locale's title. */
    title: z.string(),
  })
  .strict();

export type LegalDocumentVersion = z.infer<typeof LegalDocumentVersionSchema>;

/** A version with its body — the member reader and the admin editor both use this. */
export const LegalDocumentVersionDetailSchema = LegalDocumentVersionSchema.extend({
  /** The active locale's body. */
  body: z.string(),
  /** The document's stable key and type, so a reader needs no second request. */
  documentKey: z.string(),
  docType: LegalDocTypeSchema,
  /** Every locale — what the editor's per-locale tabs prefill from (W7). */
  translations: LegalVersionTranslationsSchema,
}).strict();

export type LegalDocumentVersionDetail = z.infer<typeof LegalDocumentVersionDetailSchema>;

/**
 * The public reader's view of a version — no draft metadata, no author.
 *
 * Separate from the admin detail rather than a field subset of it: this is the
 * payload an **unauthenticated** signup screen fetches, and a shape that cannot
 * express `publishedByUserId` cannot leak a staff id by a later refactor.
 */
export const PublicLegalDocumentSchema = z
  .object({
    documentId: UuidSchema,
    key: z.string(),
    docType: LegalDocTypeSchema,
    requiresConsent: z.boolean(),
    versionId: UuidSchema,
    version: z.number().int().min(1),
    effectiveAt: z.iso.datetime(),
    title: z.string(),
    body: z.string(),
  })
  .strict();

export type PublicLegalDocument = z.infer<typeof PublicLegalDocumentSchema>;

/** The same document without its body — the list a signup screen links from. */
export const PublicLegalDocumentSummarySchema = PublicLegalDocumentSchema.omit({
  body: true,
}).strict();

export type PublicLegalDocumentSummary = z.infer<typeof PublicLegalDocumentSummarySchema>;

/**
 * Creating a draft.
 *
 * The version **number** is absent: it is `max(version) + 1`, computed by the
 * server. A client-chosen number is either a duplicate or a gap, and both are
 * unrecoverable once a consent record points into the sequence.
 */
export const CreateLegalVersionSchema = z
  .object({
    translations: LegalVersionTranslationsSchema,
  })
  .strict();

export type CreateLegalVersionRequest = z.infer<typeof CreateLegalVersionSchema>;

/**
 * Editing a draft.
 *
 * Only the content. `documentId`, `version`, `status`, `materiality` and
 * `effectiveAt` are absent — a version does not move between documents, does
 * not renumber, and does not publish itself through an update (publishing is
 * its own endpoint, because it is the irreversible act).
 */
export const UpdateLegalVersionSchema = z
  .object({
    translations: LegalVersionTranslationsSchema,
  })
  .strict();

export type UpdateLegalVersionRequest = z.infer<typeof UpdateLegalVersionSchema>;

/**
 * Publishing (N16, N27).
 *
 * `materiality` is required and has **no default**: it decides whether every
 * member on the platform is stopped and asked to accept again, and a default
 * would make the most consequential choice in this module the one nobody had
 * to make. The admin publish dialog states the consequence in plain language
 * beside each option (`03-legal-consent.md` §admin).
 *
 * `effectiveAt` is optional and defaults to now. A future date is allowed —
 * announcing a change ahead of time is normal practice — and the consent gate
 * does not arm until it passes.
 */
export const PublishLegalVersionSchema = z
  .object({
    materiality: LegalMaterialitySchema,
    effectiveAt: z.iso.datetime().optional(),
  })
  .strict();

export type PublishLegalVersionRequest = z.infer<typeof PublishLegalVersionSchema>;

/** The whitelisted filters of the admin version list (N7). */
export const LegalVersionsListQuerySchema = makeListQuery({
  documentId: UuidSchema.optional(),
  status: LegalVersionStatusSchema.optional(),
  materiality: LegalMaterialitySchema.optional(),
});

export type LegalVersionsListQuery = z.infer<typeof LegalVersionsListQuerySchema>;
